import { DOCUMENT_AGENT_API_BASE_URL } from "../config/constants";

export type SourceLink = {
    title: string;
    url?: string | null;
    category?: string | null;
    pageNumber?: number | null;
    score?: number | null;
};

export type AgentAction = {
    label: string;
    type: string;
    value: string;
};

export type ChatResponse = {
    answer: string;
    conversationId: string;
    messageId: string;
    intent: string;
    category?: string | null;
    escalationRecommended: boolean;
    sources: SourceLink[];
    actions: AgentAction[];
};

export type UploadResponse = {
    message: string;
    conversationId: string;
    fileName: string;
    storageKey: string;
    indexed: boolean;
    indexingError?: string | null;
    chunkCount?: number | null;
};

export type ClearResponse = {
    message: string;
    clearedConversationId: string;
    conversationId: string;
    deletedFiles: number;
    removedDocuments: number;
    removedChunks: number;
};

export type FeedbackRating = "helpful" | "not_helpful" | "wrong_source" | "needs_human";

const apiUrl = (path: string) => `${DOCUMENT_AGENT_API_BASE_URL}${path}`;

// Every request is aborted after this long so a hung backend can never leave
// the UI stuck in a permanent "uploading"/"sending" state.
const UPLOAD_TIMEOUT_MS = 300_000;
const CHAT_TIMEOUT_MS = 90_000;
const CLEAR_TIMEOUT_MS = 60_000;
const FEEDBACK_TIMEOUT_MS = 30_000;

const MAX_ERROR_BODY_LENGTH = 200;
const GENERIC_ERROR_PREFIX = "The service returned status";

function looksLikeHtml(input: string): boolean {
    return /<\/?[a-z][\s\S]*>/i.test(input) || /^\s*<!doctype/i.test(input);
}

function stripHtmlTags(input: string): string {
    return input.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Turns a non-JSON error body (e.g. an HTML error page from a proxy or load
 * balancer) into something safe to show a user: short, tag-free, and a
 * generic fallback when the body is markup/garbage rather than real prose.
 */
function summarizeTextBody(rawText: string, status: number): string {
    const trimmed = rawText.trim();
    if (!trimmed || looksLikeHtml(trimmed)) {
        return `${GENERIC_ERROR_PREFIX} ${status}.`;
    }

    const truncated = trimmed.length > MAX_ERROR_BODY_LENGTH
        ? `${trimmed.slice(0, MAX_ERROR_BODY_LENGTH).trim()}…`
        : trimmed;

    return stripHtmlTags(truncated) || `${GENERIC_ERROR_PREFIX} ${status}.`;
}

function extractErrorMessage(rawText: string, isJson: boolean, status: number): string {
    if (isJson && rawText.trim()) {
        try {
            const payload: any = JSON.parse(rawText);
            const message = typeof payload === "string"
                ? payload
                : payload?.detail ?? payload?.message ?? payload?.error ?? payload?.title;
            if (message) return String(message);
        } catch {
            // Malformed JSON error body; fall through to the generic text handling below.
        }
    }

    return summarizeTextBody(rawText, status);
}

async function parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    const rawText = await response.text().catch(() => "");

    if (!response.ok) {
        throw new Error(extractErrorMessage(rawText, isJson, response.status));
    }

    if (!rawText) {
        // Some endpoints (e.g. feedback) intentionally return an empty success body.
        return {} as T;
    }

    if (!isJson) {
        throw new Error(`${GENERIC_ERROR_PREFIX} ${response.status}.`);
    }

    try {
        return JSON.parse(rawText) as T;
    } catch {
        throw new Error("The service returned an unexpected response. Please try again.");
    }
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, { ...init, signal: controller.signal });
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error("The request took too long to complete. Please try again.");
        }
        throw error;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

async function requestJson<T>(path: string, init: RequestInit, timeoutMs: number): Promise<T> {
    const response = await fetchWithTimeout(apiUrl(path), init, timeoutMs);
    return parseResponse<T>(response);
}

export async function sendChatMessage(message: string, conversationId: string): Promise<ChatResponse> {
    return requestJson<ChatResponse>("/agent/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ message, conversationId }),
    }, CHAT_TIMEOUT_MS);
}

export async function uploadKnowledgeDocument(file: File, conversationId: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append("File", file);
    formData.append("ConversationId", conversationId);

    return requestJson<UploadResponse>("/storage/upload", {
        method: "POST",
        body: formData,
    }, UPLOAD_TIMEOUT_MS);
}

export async function clearKnowledgeSession(conversationId: string): Promise<ClearResponse> {
    return requestJson<ClearResponse>("/storage/clear", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ conversationId }),
    }, CLEAR_TIMEOUT_MS);
}

export async function sendMessageFeedback(
    messageId: string,
    conversationId: string,
    rating: FeedbackRating,
    comment?: string,
): Promise<void> {
    await requestJson<void>("/agent/feedback", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        body: JSON.stringify({ messageId, conversationId, rating, comment }),
    }, FEEDBACK_TIMEOUT_MS);
}
